/**
 * The paper packet — the part of this system that still works when nothing
 * else does.
 *
 * This used to be HTML printed by the browser, which is the arrangement the
 * map sheets were moved off: two engines disagreed about the page often enough
 * that the fixes had become a list of workarounds. Steward writes the document
 * itself now, on the same page furniture as the maps, so the packet and the
 * sector sheets in the same binder look like one system.
 *
 * Two rules govern everything here, and they are why this is worth writing by
 * hand rather than styling a page:
 *   1. Every box is DRAWN, not a form control. A checkbox is four lines on
 *      paper; nothing can hide it and no stylesheet has an opinion.
 *   2. A page prints what the system knows and leaves a rule where it does
 *      not, so it is useful either way. A page of blanks is a form; a page
 *      with holes in it is neither.
 *
 * It is meant to be printed cold, before anything happens: create a planning
 * incident, seed it, print, and put it in the go-bag.
 */
import {
  COMMS_RULES, EMERGENCY, LATER, OUT_OF_AREA, PLACES, STAKE, type RefEntry,
} from '../incidentReference';
import { buildPlat, type PlatData, type PlatHousehold } from '../plat/model';
import {
  contentBox as mapContentBox, drawMapPage, fitMap, noteHeight,
  pageSize as mapPageSize, type MapPageOptions,
} from '../plat/sheet';
import { formatPhone } from '../phone';
import { PT_PER_PX, PdfDoc, type PdfPage } from '../pdf/pdf';
import {
  CAP_HEIGHT, checkbox, clip, contentBox, furniture, heading, labelled,
  notesArea, pageSize, stampFooters, writeRule, type Box,
} from '../pdf/page';

const BODY_PT = 8.5;
const LEAD = 12;
const SMALL_PT = 7;

export interface PacketIncident {
  name: string;
  kind: string | null;
  is_drill: boolean;
  current_round: number;
  activated_at: string | null;
  reporting_to: string | null;
  reporting_contact: string | null;
  commander: string | null;
  commander_phone: string | null;
  deputy: string | null;
  deputy_phone: string | null;
  staging_area: string | null;
  radio_primary: string | null;
  radio_alt: string | null;
  frs_channel: string | null;
  net_schedule: string | null;
  comms_notes: string | null;
}

export interface PacketCounts {
  people_total: number;
  accounted: number;
  unknown: number;
  missing: number;
  sites_total: number;
  sites_swept: number;
}

export interface LeadershipGroup {
  group: string;
  rows: { role: string; name: string; phone: string | null }[];
}

/** Just enough of an incident site for the packet to print it. */
export interface PacketSite {
  label: string;
  formatted_address: string | null;
  /** Already stripped of HTML, one line per line. The first is the parcel key. */
  address_lines: string[];
  accountability: string;
  contact_result: string;
  structure: string | null;
  hazards: string[];
  notes: string | null;
  last_checked_at: string | null;
  checked_by: string | null;
  swept_this_round: boolean;
  people: {
    id: string;
    name: string;
    age: number | null;
    phone: string | null;
    accounted: string;
    triage: string | null;
    disposition: string | null;
    medical_needs: string | null;
    special_needs: string[];
    contacts: { id: string; name: string; relationship: string | null; phone: string | null;
                is_out_of_area: boolean }[];
  }[];
  contacts: { id: string; name: string; relationship: string | null; phone: string | null;
              is_out_of_area: boolean }[];
}

/** Who is responsible for a sector, at whichever layer named them. */
export interface PacketAssignment {
  role: string;
  name: string | null;
  phone: string | null;
  radio: string | null;
}

export interface PacketSector {
  name: string;
  sites: PacketSite[];
  /** The sector's lead and its walker, in the order they print. */
  assignments: PacketAssignment[];
  /**
   * The walks inside this sector, when the packet is cut on them.
   *
   * A sector with walks prints its own map and then hands over: the walks
   * carry the rosters, because the roster is the page somebody fills in and
   * nobody fills in a sector-wide one while walking a third of it. A sector
   * with no walks is a leaf and carries its own.
   */
  walks?: PacketSector[];
}

export interface PacketShelter {
  label: string;
  formatted_address: string | null;
  capacity: number | null;
  people: { id: string; name: string; age: number | null; disposition: string | null;
            medical_needs: string | null; special_needs: string[];
            arrived_at: string | null }[];
}

export const SECTIONS = ['cover', 'info', 'sectors', 'log', 'shelters'] as const;
export type Section = (typeof SECTIONS)[number];
export type PacketSections = Record<Section, boolean>;

export interface PacketArgs {
  incident: PacketIncident;
  counts: PacketCounts;
  /** Current holders of the printed callings, from Steward's own records. */
  leadership: LeadershipGroup[];
  /** Where command is being run from, when a site has been named. */
  commandPost: string | null;
  ward: string;
  /** Sectors in the order they are bound, each with its own map and roster. */
  sectors: PacketSector[];
  /**
   * County parcels, for the sector maps. Absent — the asset failed to load —
   * the roster still prints; a packet without a map beats no packet.
   */
  plat: PlatData | null;
  shelters: PacketShelter[];
  /** Which parts to bind. All of them, unless somebody wants one section. */
  sections: PacketSections;
  /** Called between sheets so the browser can paint a progress line. */
  onProgress?: (done: number, total: number, label: string) => void;
}

/**
 * A page under construction: where the next line goes, and what to do when
 * the page runs out.
 *
 * Cheaper than a layout engine and honest about what these documents are —
 * a column of blocks down a page, each of which either fits or starts a new
 * one. Nothing here reflows.
 */
class Flow {
  page!: PdfPage;
  box!: Box;
  y!: number;

  constructor(private doc: PdfDoc, private title: string,
              private right: string, private landscape = false,
              private flag: string | null = null) {
    this.newPage();
  }

  newPage(): PdfPage {
    const { w, h } = pageSize(this.landscape);
    this.page = this.doc.addPage(w, h);
    furniture(this.page, this.title, this.right, this.flag);
    this.box = contentBox(w, h);
    this.y = this.box.y1 - BODY_PT;
    return this.page;
  }

  /** Start a new page if `h` points will not fit, and return the baseline. */
  need(h: number): number {
    if (this.y - h < this.box.y0) this.newPage();
    return this.y;
  }

  get w() { return this.box.x1 - this.box.x0; }
  get x() { return this.box.x0; }
}

/**
 * What rides at the right of the head bar on every page.
 *
 * A drill has to say so on each sheet, not just the cover, because pages get
 * separated and one on a clipboard has to answer the question by itself.
 */
const flagFor = (a: PacketArgs) => (a.incident.is_drill ? 'DRILL — NOT REAL' : null);

// ── 1 · Cover ─────────────────────────────────────────────────────────────

function drawCover(doc: PdfDoc, a: PacketArgs, printedAt: Date) {
  const { w, h } = pageSize(false);
  const page = doc.addPage(w, h);
  furniture(page, a.incident.name, a.incident.kind ?? '', flagFor(a));
  const box = contentBox(w, h);
  // Measured from the type's own cap height rather than a fixed drop. The 12pt
  // that used to be here was tuned when the drill banner sat in this gap; with
  // the banner gone, 20pt capitals reached past the top of the box and touched
  // the head bar.
  const WARD_PT = 20;
  let y = box.y1 - WARD_PT * CAP_HEIGHT - 10;

  page.text(a.ward, box.x0, y, { size: WARD_PT, bold: true });
  y -= 18;
  const line = [
    a.incident.kind ?? 'Incident',
    `Round ${a.incident.current_round}`,
    a.incident.activated_at
      ? `activated ${new Date(a.incident.activated_at).toLocaleString()}`
      : null,
  ].filter(Boolean).join(' · ');
  page.text(line, box.x0, y, { size: 9, gray: 0.35 });
  y -= 12;
  page.text(`Printed ${printedAt.toLocaleString()} — data as of this printing only`,
            box.x0, y, { size: SMALL_PT, gray: 0.45 });
  y -= 26;

  // What the system knew at the moment of printing. Deliberately four big
  // numbers: anything smaller is a report, and a cover is not a report.
  y = heading(page, 'Status at printing', box.x0, y, box.x1 - box.x0);
  const tiles: [string, string][] = [
    ['Accounted', `${a.counts.accounted}/${a.counts.people_total}`],
    ['Unaccounted', String(a.counts.unknown)],
    ['Missing', String(a.counts.missing)],
    ['Addresses swept', `${a.counts.sites_swept}/${a.counts.sites_total}`],
  ];
  const tw = (box.x1 - box.x0) / 4;
  y -= 6;
  tiles.forEach(([label, value], i) => {
    const x = box.x0 + i * tw;
    page.strokeGray(0.55);
    page.lineWidth(0.6);
    page.rect(x, y - 34, tw - 8, 42, 'S');
    page.text(value, x + (tw - 8) / 2, y - 14, { size: 17, bold: true, align: 'center' });
    page.text(label.toUpperCase(), x + (tw - 8) / 2, y - 28,
              { size: 6.5, align: 'center', gray: 0.4, tracking: 0.5 });
  });
  y -= 52;

  // The same figures again, as a grid to keep by hand. Paper goes stale in
  // twenty minutes; this is the part that stays true, because the person
  // holding the clipboard is the one updating it.
  y = heading(page, 'Running tally — update by hand', box.x0, y, box.x1 - box.x0);
  const rows = ['Accounted', 'Unaccounted', 'Injured (immediate)', 'Injured (delayed)',
                'Missing', 'Deceased', 'Addresses swept', 'Time'];
  const labelW = 132;
  const cols = 6;
  const cw = (box.x1 - box.x0 - labelW) / cols;
  const rh = 17;
  y -= 4;
  page.strokeGray(0.55);
  page.lineWidth(0.5);
  rows.forEach((r, i) => {
    const top = y - i * rh;
    page.rect(box.x0, top - rh, labelW, rh, 'S');
    page.text(r, box.x0 + 4, top - rh + 5.5,
              { size: i === rows.length - 1 ? 6.5 : BODY_PT,
                gray: i === rows.length - 1 ? 0.4 : 0 });
    for (let c = 0; c < cols; c++) {
      page.rect(box.x0 + labelW + c * cw, top - rh, cw, rh, 'S');
    }
  });
  y -= rows.length * rh + 22;

  // Who is running it, on the cover as well as the info page: the cover is
  // what gets handed to somebody arriving, and "who do I report to" is their
  // first question.
  y = heading(page, 'Command', box.x0, y, box.x1 - box.x0);
  const cmd: RefEntry[] = [
    { label: 'Incident commander', value: joinPhone(a.incident.commander, a.incident.commander_phone) },
    { label: 'Deputy / scribe', value: joinPhone(a.incident.deputy, a.incident.deputy_phone) },
    { label: 'Command post', value: a.commandPost ?? '' },
    { label: 'Staging area', value: a.incident.staging_area ?? '' },
    { label: 'Reporting to', value: joinPhone(a.incident.reporting_to, a.incident.reporting_contact) },
  ];
  for (const r of cmd) {
    labelled(page, r.label, r.value || null, box.x0, y, box.x1 - box.x0, 116, BODY_PT);
    y -= LEAD + 2;
  }

  y -= 12;
  if (y - box.y0 > 60) {
    const top = heading(page, 'Notes', box.x0, y, box.x1 - box.x0);
    notesArea(page, box.x0, top - 4, box.x1 - box.x0, top - 4 - box.y0);
  }
}

const joinPhone = (name: string | null, phone: string | null) =>
  [name, formatPhone(phone)].filter(Boolean).join(' — ');

// ── 2 · Info ──────────────────────────────────────────────────────────────

/**
 * Every number worth having on paper, on one page, in two columns.
 *
 * The left column is this incident — what changes every activation. The right
 * is what does not: 911, the utilities, who holds which calling. Sorted that
 * way because the left column is what somebody checks on the way out the door
 * and the right is what they come back to when something has gone wrong.
 */
function drawInfo(doc: PdfDoc, a: PacketArgs) {
  const flow = new Flow(doc, 'Contacts and comms', a.incident.name, false, flagFor(a));
  const gutter = 22;
  const colW = (flow.w - gutter) / 2;
  const left = flow.x;
  const right = flow.x + colW + gutter;

  const y = flow.box.y1 - 12;

  // ── left column ──
  let ly = heading(flow.page, 'This incident', left, y, colW);
  const command: RefEntry[] = [
    { label: 'Commander', value: joinPhone(a.incident.commander, a.incident.commander_phone) },
    { label: 'Deputy / scribe', value: joinPhone(a.incident.deputy, a.incident.deputy_phone) },
    { label: 'Command post', value: a.commandPost ?? '' },
    { label: 'Staging', value: a.incident.staging_area ?? '' },
    { label: 'Reporting to', value: joinPhone(a.incident.reporting_to, a.incident.reporting_contact) },
  ];
  ly = rows(flow.page, command, left, ly, colW, 82);

  ly -= 8;
  ly = heading(flow.page, 'Comms', left, ly, colW);
  const comms: RefEntry[] = [
    { label: 'Radio primary', value: a.incident.radio_primary ?? '' },
    { label: 'Alternate', value: a.incident.radio_alt ?? '' },
    { label: 'FRS / GMRS', value: a.incident.frs_channel ?? '' },
    { label: 'Net', value: a.incident.net_schedule ?? '' },
    OUT_OF_AREA,
  ];
  ly = rows(flow.page, comms, left, ly, colW, 82);
  if (a.incident.comms_notes) {
    ly -= 2;
    for (const t of wrap(a.incident.comms_notes, colW, SMALL_PT)) {
      flow.page.text(t, left, ly, { size: SMALL_PT, gray: 0.3 });
      ly -= LEAD - 3;
    }
  }

  // Advice, not tasks, so deliberately no boxes beside them: a checkbox next
  // to "text, do not call" says somebody is meant to tick it off once.
  ly -= 8;
  for (const rule of COMMS_RULES) {
    for (const t of wrap(rule, colW - 10, SMALL_PT)) {
      flow.page.text(t, left + 10, ly, { size: SMALL_PT, gray: 0.25 });
      ly -= LEAD - 3.5;
    }
    ly -= 3;
  }

  ly -= 10;
  ly = heading(flow.page, 'Ward leadership', left, ly, colW);
  ly = people(flow.page, a.leadership, left, ly, colW);

  // ── right column ──
  let ry = y;
  ry = heading(flow.page, 'Emergency', right, ry, colW);
  // 911 alone and large. Printing the agencies' own emergency numbers beside
  // it invites somebody to dial a slower path under stress.
  flow.page.text('911', right, ry - 6, { size: 24, bold: true });
  flow.page.text('fire · medical · police', right + 46, ry - 6, { size: SMALL_PT, gray: 0.4 });
  flow.page.text('Everything below is deliberately not 911 — the calls that',
                 right + 46, ry - 15, { size: 6, gray: 0.5 });
  flow.page.text('saturate it, and the ones a dispatcher cannot help with.',
                 right + 46, ry - 21.5, { size: 6, gray: 0.5 });
  ry -= 34;
  ry = rows(flow.page, EMERGENCY, right, ry, colW, 128);

  ry -= 8;
  ry = heading(flow.page, 'Stake', right, ry, colW);
  ry = people(flow.page, [{ group: '', rows: STAKE }], right, ry, colW);

  ry -= 8;
  ry = heading(flow.page, 'Where to go', right, ry, colW);
  ry = rows(flow.page, PLACES, right, ry, colW, 92);

  ry -= 8;
  ry = heading(flow.page, 'Days, not hours', right, ry, colW);
  ry = rows(flow.page, LATER, right, ry, colW, 118);

  // Whatever is left at the foot of the page becomes writable. What gets
  // recorded there — a road out, a shelter opened, a number that turned out
  // to be wrong — is exactly what this page did not know when it printed.
  const foot = Math.min(ly, ry) - 16;
  if (foot - flow.box.y0 > 40) {
    const top = heading(flow.page, 'Learned since printing', flow.x, foot, flow.w);
    notesArea(flow.page, flow.x, top - 4, flow.w, top - 4 - flow.box.y0);
  }
}

/**
 * Blocks of people: role, name, number — one line each.
 *
 * Read by scanning down the numbers rather than by reading the names, so the
 * numbers are flush right in a column of their own and a vacancy is a rule
 * where its number would be. The stake and the ward are set identically
 * because they are the same kind of thing, and two blocks of people set two
 * different ways on one page read as an accident.
 */
function people(page: PdfPage, groups: LeadershipGroup[], x: number, y: number,
                w: number): number {
  const PHONE_W = 76;
  for (const g of groups) {
    if (g.group) {
      page.text(g.group.toUpperCase(), x, y, { size: 6, gray: 0.45, tracking: 0.4 });
      y -= LEAD - 2.5;
    }
    const nameX = 52;
    for (const r of g.rows) {
      page.text(r.role, x + 4, y, { size: SMALL_PT, gray: 0.4 });
      if (!r.name) {
        // Nobody in the seat, or nobody has filled it in: one rule across
        // both columns. A dash where the name goes and a rule where the
        // number goes says the seat is filled by somebody called "—".
        writeRule(page, x + nameX, y - 2, w - nameX);
      } else {
        page.text(clip(r.name, w - PHONE_W - nameX - 6, BODY_PT, true), x + nameX, y,
                  { size: BODY_PT, bold: true });
        if (r.phone) {
          page.text(formatPhone(r.phone), x + w, y, { size: BODY_PT, align: 'right' });
        } else {
          writeRule(page, x + w - PHONE_W, y - 2, PHONE_W);
        }
      }
      y -= LEAD - 0.5;
    }
    y -= 5;
  }
  return y;
}

/**
 * A run of labelled lines, returning the baseline after the last of them.
 *
 * A detail line sits under whichever half it belongs to: under the value when
 * the line was answered (a name's phone number), under the label when it was
 * not (which utility to ask for), so the write-in rule stays clear either way.
 */
function rows(page: PdfPage, list: RefEntry[], x: number, y: number, w: number,
              labelW: number): number {
  for (const r of list) {
    labelled(page, r.label, r.value || null, x, y, w, labelW, BODY_PT);
    y -= LEAD;
    if (r.detail) {
      const dx = r.value ? x + labelW : x + 6;
      page.text(clip(r.detail, w - (dx - x), SMALL_PT), dx, y + 3.5,
                { size: SMALL_PT, gray: 0.45 });
      y -= LEAD - 4;
    }
  }
  return y;
}

/** Greedy wrap. The only text here long enough to need it is a note. */
function wrap(s: string, w: number, size: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of s.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (clip(next, w, size) !== next && line) { out.push(line); line = word; } else { line = next; }
  }
  if (line) out.push(line);
  return out;
}

// ── 4 · Activity log ──────────────────────────────────────────────────────

/**
 * Blank ICS-214-style sheets. The whole point is that they work with a pen.
 *
 * Two of them, because one fills up faster than anybody expects and the
 * moment you need a third is the moment you cannot print one.
 */
function drawLog(doc: PdfDoc, a: PacketArgs) {
  for (let p = 0; p < 2; p++) {
    const { w, h } = pageSize(false);
    const page = doc.addPage(w, h);
    furniture(page, 'Activity log', a.incident.name, flagFor(a));
    const box = contentBox(w, h);
    let y = box.y1 - 12;

    labelled(page, 'Recorder', null, box.x0, y, (box.x1 - box.x0) / 2 - 10, 48, BODY_PT);
    labelled(page, 'Sheet', null, box.x0 + (box.x1 - box.x0) / 2 + 10, y,
             (box.x1 - box.x0) / 2 - 10, 34, BODY_PT);
    y -= LEAD + 8;

    const cols: [string, number][] = [
      ['Time', 44], ['From / to', 96], ['Entry', 0], ['Done', 34],
    ];
    const fixed = cols.reduce((n, [, cw]) => n + cw, 0);
    const entryW = box.x1 - box.x0 - fixed;
    const widths = cols.map(([, cw]) => (cw === 0 ? entryW : cw));

    page.strokeGray(0);
    page.lineWidth(0.8);
    page.segments([[{ x: box.x0, y: y - 3 }, { x: box.x1, y: y - 3 }]]);
    let cx = box.x0;
    cols.forEach(([label], i) => {
      page.text(label.toUpperCase(), cx + 3, y, { size: 6.5, bold: true, tracking: 0.5 });
      cx += widths[i];
    });
    y -= 12;

    const rh = 21;
    const n = Math.floor((y - box.y0) / rh);
    page.strokeGray(0.5);
    page.lineWidth(0.4);
    for (let i = 0; i < n; i++) {
      const top = y - i * rh;
      page.segments([[{ x: box.x0, y: top - rh }, { x: box.x1, y: top - rh }]]);
      let dx = box.x0;
      widths.forEach((cw, ci) => {
        dx += cw;
        if (ci < widths.length - 1) {
          page.segments([[{ x: dx, y: top }, { x: dx, y: top - rh }]]);
        }
      });
      checkbox(page, box.x1 - widths[3] / 2 - 4, top - rh + 6, 8, false);
    }
  }
}

// ── 3 · Sectors ───────────────────────────────────────────────────────────

/**
 * The key the parcel asset was built with (see refresh_parcels.py): the first
 * address line, uppercased.
 *
 * Taken from address_lines rather than from formatted_address, because an
 * incident site's address has already had its <br/> turned into ", " on the
 * way in (_strip_html, in the API) — so splitting the formatted string on
 * <br/> matched nothing, every parcel came back not-mine, and every sector
 * printed the whole ward with no shading. The comma split is the fallback for
 * a site added by hand with no lines of its own.
 */
const addrKey = (site: PacketSite) =>
  (site.address_lines?.[0] ?? (site.formatted_address ?? '').split(',')[0])
    .replace(/\s+/g, ' ').trim().toUpperCase();

/** HTML out of an address that was stored with <br/> line breaks. */
const plain = (s: string | null) =>
  (s ?? '').replace(/<br\s*\/?>/gi, ', ').replace(/<[^>]*>/g, '').trim();

/**
 * The line above a sector's map: who has it, on what channel, and when they
 * started. Returns the new top of the box, so the map is fitted to what is
 * left rather than drawn over it.
 */
function sectorSubhead(a: PacketArgs, sector: PacketSector) {
  return (page: PdfPage, box: Box): number => {
    const w = box.x1 - box.x0;
    const y = box.y1 - 8;
    // Radio, start and end are fixed-width at the right; the two people share
    // everything left over. Reserved before the names are laid out, or the
    // last rule runs off the edge of the page. Assigned names print; the rest
    // is a rule, because a sector handed over mid-incident is filled in at
    // the tailgate, not in the app.
    const FIXED = 90 + 60 + 56;
    const wide = (w - FIXED) / sector.assignments.length;
    let x = box.x0;
    // The phone goes under the name rather than beside it. Beside it, the two
    // together were wider than any honest column and the number — the half
    // that has to be dialled — was the half that got the ellipsis.
    const cell = (label: string, value: string | null, labelW: number, cw: number,
                  sub?: string | null) => {
      page.text(label, x, y, { size: 6.5, gray: 0.45 });
      if (value) {
        page.text(clip(value, cw - labelW - 8, SMALL_PT, true), x + labelW, y,
                  { size: SMALL_PT, bold: true });
      } else {
        writeRule(page, x + labelW, y - 2, cw - labelW - 8);
      }
      if (sub) {
        page.text(clip(sub, cw - labelW - 8, 6), x + labelW, y - 7.5,
                  { size: 6, gray: 0.35 });
      }
      x += cw;
    };
    for (const as of sector.assignments) {
      cell(as.role, as.name, 36, wide, formatPhone(as.phone));
    }
    cell('Radio', sector.assignments.map((as) => as.radio).find(Boolean) ?? null, 30, 90);
    cell('Start', null, 26, 60);
    cell('End', null, 22, 56);
    void a;
    return box.y1 - 26;
  };
}

/**
 * A map page, drawn on the plat sheets' own engine.
 *
 * Two passes, as the sector maps are: the first learns which way the sheet
 * turns and how big the map will be, and the second is built knowing that,
 * because every label-fitting decision in the model is measured against the
 * scale it will actually be drawn at.
 *
 * The plat model divides households by their macro/micro sector, so an
 * incident's own sector value is handed to it as both. Which layer the value
 * came from is the caller's business, not the map's. The ward sheet passes
 * the ward focus instead, which drops the house numbers — 1,600 of them do
 * not fit — and leaves the shading to say where the members are.
 */
function drawMap(doc: PdfDoc, a: PacketArgs, o: {
  sites: PacketSite[];
  title: string;
  sectorName: string | null;
  subhead?: MapPageOptions['subhead'];
}) {
  if (!a.plat) return;
  const households: PlatHousehold[] = o.sites
    .filter((s) => s.formatted_address || s.address_lines?.length)
    .map((s) => ({
      key: addrKey(s),
      label: s.label,
      lat: null, lng: null,
      oldestAge: null,
      macro: o.sectorName ?? '',
      micro: o.sectorName ?? '',
    }));
  if (!households.length) return;

  const common = {
    data: a.plat,
    households,
    focus: o.sectorName
      ? { kind: 'macro' as const, name: o.sectorName }
      : { kind: 'ward' as const },
    showNames: !!o.sectorName,
  };
  const flags = { showShading: true, showNames: !!o.sectorName };

  const probe = buildPlat({ ...common, orientation: 'auto' });
  const { w, h } = mapPageSize(probe.landscape);
  const box = mapContentBox(w, h);
  const fit = fitMap(probe.frame, box, noteHeight(probe, flags, box.x1 - box.x0));
  const px = fit.w / PT_PER_PX;
  const model = buildPlat({
    ...common,
    orientation: probe.landscape ? 'landscape' : 'portrait',
    sheetPx: { portrait: px, landscape: px },
  });

  const people = o.sites.reduce((n, s) => n + s.people.length, 0);
  drawMapPage(doc.addPage(w, h), model, {
    title: o.title,
    subtitle: `${o.sites.length} addresses · ${people} people`,
    flags,
    subhead: o.subhead,
  });
}

/**
 * The columns of the accountability table, in points.
 *
 * Landscape, because this is the page somebody fills in on a clipboard and it
 * has eight columns. Portrait fitted them by squeezing the notes column to an
 * inch and a half, which is the one column that has to take a sentence
 * written with a pen on a car bonnet.
 */
const COLS = {
  site: 240, acct: 34, home: 44, missing: 42, injured: 38,
  structure: 56, hazards: 72,
} as const;

/** Column heads, each centred over the boxes it names. */
function rosterHead(page: PdfPage, box: Box, y: number): number {
  const heads: [string, number, boolean][] = [
    ['Address / occupants', COLS.site, false], ['Acct', COLS.acct, true],
    ['Not home', COLS.home, true], ['Missing', COLS.missing, true],
    ['Injured', COLS.injured, true], ['Structure', COLS.structure, false],
    ['Hazards', COLS.hazards, false], ['Notes / time', 0, false],
  ];
  let x = box.x0;
  for (const [label, w, centred] of heads) {
    page.text(label.toUpperCase(), centred ? x + w / 2 : x, y,
              { size: 5.6, bold: true, gray: 0.35, tracking: 0.3,
                align: centred ? 'center' : 'left' });
    x += w;
  }
  page.dash(0, 0);
  page.strokeGray(0);
  page.lineWidth(0.8);
  page.segments([[{ x: box.x0, y: y - 3 }, { x: box.x1, y: y - 3 }]]);
  return y - 11;
}

/**
 * One row per address, with a tick box per occupant.
 *
 * Pre-ticked from what the board already knows, so a runner can see at a
 * glance what the last pass found without going back to a screen — and the
 * empty boxes beside them stay writable.
 */
function drawSectorRoster(doc: PdfDoc, a: PacketArgs, sector: PacketSector) {
  const flow = new Flow(doc, sector.name, `${sector.sites.length} addresses`, true, flagFor(a));
  let y = rosterHead(flow.page, flow.box, flow.box.y1 - 10);

  for (const site of sector.sites) {
    const rowH = 16 + site.people.length * 9.5;
    if (y - rowH < flow.box.y0) {
      flow.newPage();
      y = rosterHead(flow.page, flow.box, flow.box.y1 - 10);
    }
    const page = flow.page;
    const x = flow.box.x0;
    const top = y;

    page.text(clip(site.label, COLS.site - 4, 8, true), x, y, { size: 8, bold: true });
    y -= 9;
    page.text(clip(plain(site.formatted_address), COLS.site - 4, 6.5), x, y,
              { size: 6.5, gray: 0.4 });
    y -= 9.5;
    for (const p of site.people) {
      checkbox(page, x + 1, y - 1, 6.5, p.accounted === 'accounted');
      const age = p.age != null ? ` ${p.age}` : '';
      const mark = p.medical_needs || p.special_needs.length ? ' +' : '';
      const flag = p.accounted === 'missing' ? ' MISSING'
        : p.accounted === 'not_home' ? ' (not home)' : '';
      const triage = p.triage ? ` [${p.triage.toUpperCase()}]` : '';
      page.text(clip(`${p.name}${age}${flag}${triage}${mark}`, COLS.site - 14, 7),
                x + 11, y, { size: 7, bold: !!p.triage });
      y -= 9.5;
    }
    if (!site.people.length) {
      page.text('occupants unknown', x + 11, y, { size: 7, gray: 0.45 });
      y -= 9.5;
    }

    // The site-level boxes sit on the address line, not the occupant lines.
    let cx = x + COLS.site;
    const box9 = (w: number, ticked: boolean) => {
      checkbox(page, cx + (w - 10) / 2, top - 9, 10, ticked);
      cx += w;
    };
    box9(COLS.acct, site.accountability === 'all_accounted');
    box9(COLS.home, site.contact_result === 'no_answer');
    box9(COLS.missing, site.accountability === 'missing');
    box9(COLS.injured, site.people.some((p) => p.triage === 'red' || p.triage === 'yellow'));

    (['green', 'yellow', 'red'] as const).forEach((g, i) => {
      checkbox(page, cx + i * 18, top - 8, 7, site.structure === g);
      page.text(g[0].toUpperCase(), cx + i * 18 + 9, top - 7, { size: 6, gray: 0.4 });
    });
    cx += COLS.structure;

    HAZARD_BOXES.forEach((hz, i) => {
      const hx = cx + (i % 2) * 36;
      const hy = top - 8 - Math.floor(i / 2) * 9;
      checkbox(page, hx, hy, 6, site.hazards.includes(hz));
      page.text(hz.slice(0, 4), hx + 8, hy + 1, { size: 5.5, gray: 0.4 });
    });
    cx += COLS.hazards;

    // What the board already knows, above the space for whatever this pass
    // turns up.
    const noteW = flow.box.x1 - cx;
    let ny = top;
    if (site.swept_this_round && site.last_checked_at) {
      const t = new Date(site.last_checked_at)
        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      page.text(clip(`checked ${t}${site.checked_by ? ` · ${site.checked_by.split('@')[0]}` : ''}`,
                     noteW, 6), cx, ny, { size: 6, gray: 0.4 });
      ny -= 8;
    }
    if (site.notes) {
      for (const line of wrap(site.notes, noteW, 6).slice(0, 2)) {
        page.text(line, cx, ny, { size: 6, gray: 0.2 });
        ny -= 7;
      }
    }

    // A hairline under every address, so a row reads across a wide page.
    page.dash(0, 0);
    page.strokeGray(0.6);
    page.lineWidth(0.3);
    page.segments([[{ x: flow.box.x0, y: y + 3 }, { x: flow.box.x1, y: y + 3 }]]);
    y -= 5;
  }

  flow.y = y;
  drawSectorDetail(flow, sector);
}

/** The four hazards worth a box on paper; the rest go in the notes column. */
const HAZARD_BOXES = ['gas', 'fire', 'water', 'structural'];

/**
 * Medical needs and emergency contacts, printed with the sector they belong
 * to rather than in an appendix.
 *
 * A runner carrying one sector should have everything for it in their hand.
 * Only addresses with something recorded — printing all of them wastes paper
 * somebody has to carry, and buries the ones that matter.
 */
function drawSectorDetail(flow: Flow, sector: PacketSector) {
  const notable = sector.sites.filter((s) =>
    s.contacts.length > 0
    || s.people.some((p) => p.medical_needs || p.special_needs.length > 0
                            || p.contacts.length > 0));
  if (!notable.length) return;

  let y = flow.need(40) - 12;
  y = heading(flow.page, 'Detail — medical needs and emergency contacts', flow.x, y, flow.w);
  flow.page.text('Sensitive: includes health information about people who are not members.',
                 flow.x, y, { size: 6, gray: 0.45 });
  y -= 11;

  for (const s of notable) {
    const lines: string[] = [];
    for (const p of s.people) {
      const needs = [...p.special_needs, p.medical_needs].filter(Boolean).join(', ');
      if (needs) lines.push(`+ ${p.name} — ${needs}`);
      for (const c of p.contacts) {
        lines.push(`tel ${c.name}${c.relationship ? ` (${c.relationship})` : ''} `
          + `${formatPhone(c.phone)}${c.is_out_of_area ? ' [out-of-area]' : ''}`);
      }
    }
    for (const c of s.contacts) {
      lines.push(`tel ${c.name}${c.relationship ? ` (${c.relationship})` : ''} `
        + `${formatPhone(c.phone)}${c.is_out_of_area ? ' [out-of-area]' : ''}`);
    }
    if (!lines.length) continue;

    const h = 10 + lines.length * 8.5;
    if (y - h < flow.box.y0) { flow.newPage(); y = flow.box.y1 - 10; }
    flow.page.text(clip(`${s.label} — ${plain(s.formatted_address)}`, flow.w, 7, true),
                   flow.x, y, { size: 7, bold: true });
    y -= 8.5;
    for (const l of lines) {
      flow.page.text(clip(l, flow.w - 8, 6.5), flow.x + 8, y, { size: 6.5, gray: 0.15 });
      y -= 8.5;
    }
    y -= 3;
  }
  flow.y = y;
}

// ── 5 · Shelters ──────────────────────────────────────────────────────────

/**
 * A sign-in sheet that already knows who is there.
 *
 * Printed with the current occupancy filled in and blank rows underneath,
 * because a shelter sheet has to work both ways: as a record of who came
 * through the app, and as the paper the door is run from when nobody is at a
 * laptop. Departure gets its own column so the sheet can answer "who is still
 * here" without a second pass.
 */
function drawShelters(doc: PdfDoc, a: PacketArgs) {
  for (const f of a.shelters) {
    const flow = new Flow(doc, `Shelter — ${f.label}`,
                          `${f.people.length}${f.capacity != null ? ` of ${f.capacity}` : ''} at printing`,
                          false, flagFor(a));
    let y = flow.box.y1 - 10;
    if (f.formatted_address) {
      flow.page.text(plain(f.formatted_address), flow.x, y, { size: 7, gray: 0.4 });
      y -= 12;
    }
    labelled(flow.page, 'Shelter manager', null, flow.x, y, flow.w / 2 - 10, 74, BODY_PT);
    labelled(flow.page, 'Opened', null, flow.x + flow.w / 2 + 10, y, flow.w / 2 - 10, 44, BODY_PT);
    y -= 18;

    const cols: [string, number][] = [
      ['Name', 150], ['Age', 26], ['Household / from', 120],
      ['Medical / special needs', 0], ['In', 34], ['Out', 34],
    ];
    const fixed = cols.reduce((n, [, w]) => n + w, 0);
    const widths = cols.map(([, w]) => (w === 0 ? flow.w - fixed : w));
    let hx = flow.x;
    cols.forEach(([label], i) => {
      flow.page.text(label.toUpperCase(), hx, y, { size: 5.6, bold: true, gray: 0.35, tracking: 0.3 });
      hx += widths[i];
    });
    flow.page.dash(0, 0);
    flow.page.strokeGray(0);
    flow.page.lineWidth(0.8);
    flow.page.segments([[{ x: flow.x, y: y - 3 }, { x: flow.box.x1, y: y - 3 }]]);
    y -= 14;

    const rh = 15;
    // One row's worth held back for the note at the foot, so it never lands
    // on top of the last line somebody wrote on.
    const rows = Math.floor((y - flow.box.y0 - 12) / rh);
    for (let i = 0; i < rows; i++) {
      const p = f.people[i];
      const top = y - i * rh;
      if (p) {
        const cells = [
          p.name,
          p.age != null ? String(p.age) : '',
          p.disposition ?? '',
          [...p.special_needs, p.medical_needs].filter(Boolean).join(', '),
          p.arrived_at
            ? new Date(p.arrived_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '',
          '',
        ];
        let cx = flow.x;
        cells.forEach((c, ci) => {
          if (c) flow.page.text(clip(c, widths[ci] - 4, 7), cx, top - 9, { size: 7 });
          cx += widths[ci];
        });
      }
      flow.page.strokeGray(0.55);
      flow.page.lineWidth(0.3);
      flow.page.segments([[{ x: flow.x, y: top - rh + 2 }, { x: flow.box.x1, y: top - rh + 2 }]]);
    }
    flow.page.text('Anyone signed in here is accounted for. Send names back to the commander '
      + 'so the board matches this sheet.', flow.x, flow.box.y0 + 2, { size: 6, gray: 0.45 });
  }
}

// ── The document ──────────────────────────────────────────────────────────

export async function buildPacketPdf(a: PacketArgs): Promise<Blob> {
  const doc = new PdfDoc(`${a.ward} — ${a.incident.name} packet`);
  const printedAt = new Date();

  if (a.sections.cover) drawCover(doc, a, printedAt);
  if (a.sections.info) drawInfo(doc, a);

  if (a.sections.sectors) {
    // Three scales, outward in: the whole ward, so somebody can see how the
    // sectors sit together; then each sector; then the walks inside it. A
    // walker needs the walk, and the person handing out the walks needs the
    // sector they came from.
    a.onProgress?.(0, a.sectors.length, a.ward);
    drawMap(doc, a, {
      sites: a.sectors.flatMap((s) => s.sites),
      title: a.ward,
      sectorName: null,
    });
  }

  for (let i = 0; a.sections.sectors && i < a.sectors.length; i++) {
    const sector = a.sectors[i];
    // A sector with nothing in it is a name, not a sheet. The route cannot
    // produce one — its groups are built from sites — but a caller passing
    // sectors from elsewhere can, and a blank roster page is pure waste in a
    // document somebody carries.
    if (!sector.sites.length) continue;
    a.onProgress?.(i, a.sectors.length, sector.name);
    drawMap(doc, a, {
      sites: sector.sites,
      title: sector.name,
      sectorName: sector.name,
      subhead: sectorSubhead(a, sector),
    });

    if (sector.walks?.length) {
      for (const walk of sector.walks) {
        if (!walk.sites.length) continue;
        drawMap(doc, a, {
          sites: walk.sites,
          title: walk.name,
          sectorName: walk.name,
          subhead: sectorSubhead(a, walk),
        });
        drawSectorRoster(doc, a, walk);
        await new Promise((r) => setTimeout(r, 0));
      }
    } else {
      drawSectorRoster(doc, a, sector);
    }
    // Let the browser paint the progress line before the next sheet.
    await new Promise((r) => setTimeout(r, 0));
  }

  if (a.sections.log) drawLog(doc, a);
  if (a.sections.shelters) drawShelters(doc, a);
  a.onProgress?.(a.sectors.length, a.sectors.length, 'writing');

  // The foot says when this was printed rather than today's date: a packet is
  // a snapshot, and the question somebody asks of a page they found on a
  // dashboard is how old it is.
  stampFooters(doc, a.incident.is_drill ? `${a.incident.name} — DRILL` : a.incident.name,
               `Printed ${printedAt.toLocaleString()}`);
  return doc.blob();
}

export const packetFilename = (name: string, d = new Date()) => {
  const p = (n: number) => String(n).padStart(2, '0');
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'incident'}-packet-${d.getFullYear()}-${p(d.getMonth() + 1)}`
    + `-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.pdf`;
};
